'use client'

import { PDFPreview } from '@chain-zen/chat'
import Image from 'next/image'
import { FileIcon } from 'react-file-utils'

import type { IShipmentFile } from '../../../../../types/shipment'
import { normalizeFileURL } from '../../../../../utils/download-file'
import {
  getDisplayFileName,
  getMimeTypeFromFileName,
  isImagePreview,
  isPdfPreview,
  isVideoPreview
} from './files-details.utils'

interface ShipmentFilePreviewPaneProps {
  file: IShipmentFile
  label: string
}

export const ShipmentFilePreviewPane = ({ file, label }: ShipmentFilePreviewPaneProps) => {
  const normalizedFileURL = normalizeFileURL(file.url)
  const displayFileName = getDisplayFileName(file.url.split('/').pop()?.split('?')[0] || 'File')

  if (isImagePreview(file.url)) {
    return (
      <div className="relative h-56 w-full overflow-hidden rounded-xl border bg-muted/10 sm:h-72 md:h-80">
        <Image
          src={normalizedFileURL}
          alt={`${label} preview`}
          fill={true}
          unoptimized={true}
          className="object-contain"
          sizes="(max-width: 1024px) 100vw, 65vw"
        />
      </div>
    )
  }

  if (isPdfPreview(file.url)) {
    return (
      <div className="h-[38vh] min-h-64 overflow-hidden rounded-xl border bg-muted/10 sm:h-[42vh] md:h-[46vh]">
        <PDFPreview src={normalizedFileURL} />
      </div>
    )
  }

  if (isVideoPreview(file.url)) {
    return (
      <video
        src={normalizedFileURL}
        controls={true}
        className="h-[38vh] min-h-64 w-full rounded-xl border bg-background sm:h-[42vh] md:h-[46vh]"
      />
    )
  }

  return (
    <div className="flex min-h-80 flex-col items-center justify-center rounded-xl border border-dashed bg-muted/10 px-6 py-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-xl border bg-background text-muted-foreground">
        <FileIcon filename={displayFileName} mimeType={getMimeTypeFromFileName(displayFileName)} size={28} />
      </div>
      <p className="mt-4 text-sm font-medium text-foreground">Preview is not available for this file type.</p>
      <p className="mt-2 max-w-md break-words text-sm text-muted-foreground">{displayFileName}</p>
    </div>
  )
}
